<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      :items="displayeXMLs"
      :isSearchable="false"
      :headers="tableHeaders"
      :disablePagination="true"
      :itemActions="actions"
      :pagination="pagination"
      :multiple="false"
      :isCustomIcon="true"
      :isInfinitScroll="true"
      hideDefaultFooter
      :filterPlaceHolder="$t('laboratory.labels.filterBy')"
      :title="$t('laboratory.xmlReport')"
      :sortableColumns="sortableColumns"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      customClass="associatedProducts"
      @iconAction="onClickAction"
    >
      <template v-slot:[`item.dateOfTransmission`]="{ item }">
        <span> {{ formatDate(item.dateOfTransmission) }}</span>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <span>{{ item.status.name }}</span>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <span
          class="file"
          @click.stop.prevent="saveFileInDisk(item.xmlFile)"
        >{{ item.xmlFile.fileName }}</span>
      </template>
      <template v-slot:[`item.file`]="{ item }">
        <span
          class="file"
          @click.stop.prevent="saveFileInDisk(item.confirmationFile)"
        >{{ item.confirmationFile.fileName }}</span>
      </template>
      <template v-slot:[`item.dateOfConfirmation`]="{ item }">
        <span> {{ formatDate(item.dateOfConfirmation) }}</span>
      </template>
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
          :saveFilter="true"
          filterName="XMLReports"
          @onApplyFilters="filterChanged"
          @onSearch="onSearchInput"
        />
      </template>
    </VTableRemote>
  </div>
</template>
<script>

import Vue from 'vue';
import { mapGetters } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import CustomFileInput from '@/components/shared/CustomFileInput';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { debounce } from 'lodash';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import filterMixin from '@/mixins/filterMixin';

export default {
    name: 'SamplesRegister',
    components: {
        VTableRemote,
        SearchWithFilters,
    },
    mixins: [
        filterMixin,
    ],
    data() {
        return {
            xmls: [],
            loading: false,
            pagination: { page: 1 },
            queryParams: { perpage: 25 },
            debouceLoadData: debounce(this.getXMLs, 500),
            filters: [
                {
                    key: 'status',
                    label: 'laboratory.labels.type',
                    type: 'select',
                    value: [],
                    multiple: false,
                    items: [
                        {
                            text: 'laboratory.labels.created',
                            value: 'CREATED',
                        },
                        {
                            text: 'laboratory.labels.sent',
                            value: 'SENT',
                        },
                    ],
                    datacy: 'XMLFilter',
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
        ]),
        headers() {
            return [
                'name',
                'dateOfTransmission',
                'status',
                'file',
                'dateOfConfirmation',
            ];
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`laboratory.labels.${header}`),
                value: header,
            }));
        },
        displayeXMLs() {
            return this.xmls.length ? this.xmls
                .map(xml => ({
                    ...xml,
                    status: {
                        key: xml.status,
                        name: this.$t(`laboratory.labels.${xml.status.toLowerCase()}`),
                    },
                    isConfirmationFileExist: !!xml.confirmationFile.id.length,
                })) : [];
        },
        actions() {
            const actions = [];

            actions.push({
                name: this.$t('laboratory.labels.joinFIle'),
                icon: 'add',
                iconAndTextColor: '#26384B',
                customValidationProperty: 'isConfirmationFileExist',
                showOncustomValidationProperty: false,
            });

            actions.push({
                name: this.$t('laboratory.labels.deleteFile'),
                icon: 'delete',
                iconAndTextColor: 'red',
                customValidationProperty: 'isConfirmationFileExist',
                showOncustomValidationProperty: true,
            });
            return actions;
        },

        sortableColumns() {
            return this.headers.map(column => this.$options.filters.lowercase(column));
        },
    },
    async mounted() {
        window.onscroll = () => {
            if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight) {
                if (this.xmls.length < this.pagination.totalElementCount) {
                    this.queryParams = {
                        ...this.queryParams,
                        page: this.pagination.currentPage + 1,
                    };
                    this.debouceLoadData();
                }
            }
        };
    },
    async created() {
        this.loading = true;
        this.getFilterValues(true);
        await this.getXMLs();
        this.loading = false;
    },
    methods: {
        async getXMLs(overwrite = false) {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            const { data } = await this.$rq.getXMLs(cleanQueryParams);
            this.xmls = data.xmls.length && !overwrite ? [...this.xmls, ...data.xmls] : data.xmls;
            this.pagination = data.pagination;
        },
        formatDate(dt) {
            return dt ? this.$options.filters.formatDate(dt, 'L') : '';
        },
        onClickAction(item, action) {
            switch (action.icon) {
                case 'delete': {
                    this.$root.$emit('showModal', {
                        width: 700,
                        title: this.$t('xmlReport.removeTitle'),
                        isForm: false,
                        payload: { msg: this.$t('xmlReport.confirmRemove') },
                        component: Vue.component('ConfirmForm', ConfirmForm),
                        onConfirm: () => this.handleDeleteFile(item.id),
                    });
                    break;
                }
                case 'add': {
                    const payload = {
                        placeholder: this.$t('generic.uploadFile'),
                        accept: 'application/pdf, image/jpeg',
                    };
                    this.$root.$emit('showModal', {
                        title: this.$t('xmlReport.addTitle'),
                        component: Vue.component('ConfirmForm', CustomFileInput),
                        payload,
                        onConfirm: file => this.handleFileUpload(item.id, file),
                        isForm: false,
                        width: 700,
                    });
                    break;
                }
                default:
                    break;
            }
        },
        filterChanged(value) {
            if (value.reset) {
                this.getFilterValues(true);
            }
            this.queryParams.status = value.status;
            this.debouceLoadData(true);
        },
        async saveFileInDisk(content) {
            const { data } = await this.$rq.downloadFile(content.id);
            this.$utils.saveFileOnDisk(data, content.fileName);
        },
        async onSearchInput(search) {
            this.queryParams = { ...this.queryParams, search };
            await this.debouceLoadData(true);
        },
        async handleFileUpload(fileId, confirmationFile) {
            if (!['application/pdf', 'image/jpeg'].includes(confirmationFile.type)) {
                this.$toasted.global.toast_error({
                    message: this.$t('xmlReport.noValid'),
                });
                return;
            }

            const formData = new FormData();
            formData.append('file', confirmationFile);

            const { data } = await this.$rq.addRBQConfirmation(fileId, formData);
            if (data) {
                this.getXMLs(true);
            }
        },
        async handleDeleteFile(fileId) {
            const { data } = await this.$rq.deleteRBQConfirmation(fileId);
            if (data) {
                this.getXMLs(true);
            }
        },
    },
};
</script>
<style lang="scss" scoped>
    .file{
        text-decoration: underline;
    }
</style>
